import { useEffect, useMemo, useState } from 'react'
import { Link, useOutletContext, useParams } from 'react-router-dom'

import { getConfigTester, stopConfigTester, updateConfigTester } from '../../../api/configTesters.js'
import { listAbTests } from '../../../api/abTests.js'
import { listGroupConfigs, listGroupConfigVersions } from '../../../api/groupConfigs.js'
import { listSegmentations } from '../../../api/segmentations.js'
import { USER_PARAM_TYPES, formatVariantLabel } from './configTesterFormUtils.js'

async function loadRemoteConfigs(applicationId) {
  const groupConfigs = await listGroupConfigs(applicationId)
  if (!Array.isArray(groupConfigs) || groupConfigs.length === 0) return []
  const versionGroups = await Promise.all(
    groupConfigs.map((gc) => listGroupConfigVersions(applicationId, gc.id)),
  )
  return versionGroups.flat().sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
}

let idSeq = 0
function nextId(prefix) {
  idSeq += 1
  return `${prefix}_${idSeq}_${Date.now()}`
}

function formatDate(value) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}

export default function ConfigTesterDetailPage() {
  const { applicationId } = useOutletContext()
  const { configTesterId } = useParams()

  const [state, setState] = useState({ status: 'loading', tester: null, error: null })
  const [refs, setRefs] = useState({ remoteConfigs: [], segmentations: [], abTests: [] })
  const [values, setValues] = useState({
    remoteConfigId: '',
    segmentationId: '',
    description: '',
    expire: '25920000',
  })
  const [abTestRows, setAbTestRows] = useState([])
  const [userParamRows, setUserParamRows] = useState([])
  const [saving, setSaving] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading', tester: null, error: null })

    Promise.all([
      getConfigTester(applicationId, configTesterId),
      loadRemoteConfigs(applicationId),
      listSegmentations(applicationId, { includeDefaults: true }),
      listAbTests(applicationId).catch(() => []),
    ])
      .then(([tester, remoteConfigs, segmentations, abTests]) => {
        if (cancelled) return
        setRefs({ remoteConfigs, segmentations, abTests: Array.isArray(abTests) ? abTests : [] })
        setState({ status: 'ok', tester, error: null })
        setValues({
          remoteConfigId: tester.remoteConfigId || '',
          segmentationId: tester.segmentationId || '',
          description: tester.description || '',
          expire: String(tester.expire ?? 25920000),
        })
        setAbTestRows(
          (tester.abTests || []).map((t) => ({
            _id: nextId('ab'),
            abTestId: t.abTestId || '',
            abVariantId: t.abVariantId || '',
          })),
        )
        setUserParamRows(
          (tester.userParams || []).map((p) => ({
            _id: nextId('up'),
            paramType: p.paramType || '',
            paramValue: p.paramValue || '',
          })),
        )
      })
      .catch((err) => {
        if (cancelled) return
        setState({ status: 'error', tester: null, error: err })
      })

    return () => { cancelled = true }
  }, [applicationId, configTesterId])

  const isActive = state.tester?.isActive ?? false

  const availableSegmentations = useMemo(() => {
    const rc = refs.remoteConfigs.find((r) => String(r.id) === String(values.remoteConfigId))
    if (!rc?.groupConfigId) return refs.segmentations
    return refs.segmentations.filter(
      (s) => !s.groupConfigId || String(s.groupConfigId) === String(rc.groupConfigId),
    )
  }, [refs, values.remoteConfigId])

  function onChange(field) {
    return (e) => {
      const v = e.target.value
      setValues((prev) => {
        const next = { ...prev, [field]: v }
        if (field === 'remoteConfigId') next.segmentationId = ''
        return next
      })
    }
  }

  function addAbTestRow() {
    setAbTestRows((prev) => [...prev, { _id: nextId('ab'), abTestId: '', abVariantId: '' }])
  }
  function removeAbTestRow(rowId) {
    setAbTestRows((prev) => prev.filter((r) => r._id !== rowId))
  }
  function changeAbTestRow(rowId, field, value) {
    setAbTestRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r
        const next = { ...r, [field]: value }
        if (field === 'abTestId') next.abVariantId = ''
        return next
      }),
    )
  }

  function addUserParamRow() {
    setUserParamRows((prev) => [...prev, { _id: nextId('up'), paramType: '', paramValue: '' }])
  }
  function removeUserParamRow(rowId) {
    setUserParamRows((prev) => prev.filter((r) => r._id !== rowId))
  }
  function changeUserParamRow(rowId, field, value) {
    setUserParamRows((prev) => prev.map((r) => (r._id === rowId ? { ...r, [field]: value } : r)))
  }

  async function handleSave(e) {
    e.preventDefault()
    if (saving) return
    setError(null)
    try {
      setSaving(true)
      const updated = await updateConfigTester(applicationId, configTesterId, {
        remoteConfigId: values.remoteConfigId || null,
        segmentationId: values.segmentationId || null,
        description: values.description,
        expire: values.expire,
        abTests: abTestRows.filter((r) => r.abTestId && r.abVariantId),
        userParams: userParamRows.filter((r) => r.paramType && r.paramValue),
      })
      setState((prev) => ({ ...prev, tester: updated }))
    } catch (err) {
      const detail = err?.body?.detail
      setError(new Error(detail ? String(detail) : String(err?.message || err)))
    } finally {
      setSaving(false)
    }
  }

  async function handleStop() {
    if (stopping) return
    if (!window.confirm('Stop this config tester?')) return
    setError(null)
    try {
      setStopping(true)
      const updated = await stopConfigTester(applicationId, configTesterId)
      setState((prev) => ({ ...prev, tester: updated }))
    } catch (err) {
      const detail = err?.body?.detail
      setError(new Error(detail ? String(detail) : String(err?.message || err)))
    } finally {
      setStopping(false)
    }
  }

  if (state.status === 'loading') {
    return <section className="card sectionCard"><div className="cardTitle">Loading...</div></section>
  }
  if (state.status === 'error' || !state.tester) {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Config tester not found</div>
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error || '')}</pre>
        <Link to={`/applications/${applicationId}/config-tester`} className="button buttonGhost">Back</Link>
      </section>
    )
  }

  const tester = state.tester

  return (
    <div className="page">
      <div className="pageHeader">
        <div>
          <h1 className="pageTitle">Config tester: {tester.qaId}</h1>
          <span style={{ fontWeight: 600, color: isActive ? '#16a34a' : '#888' }}>
            {isActive ? 'Active' : 'Off'}
          </span>
        </div>
        <div className="buttonGroup">
          {isActive ? (
            <button
              type="button"
              className="button buttonGhost buttonDanger"
              disabled={stopping}
              onClick={handleStop}
            >
              {stopping ? 'Stopping...' : 'Stop'}
            </button>
          ) : null}
          <Link to={`/applications/${applicationId}/config-tester`} className="button buttonGhost">Back</Link>
        </div>
      </div>

      {error ? <div className="formError">{String(error?.message || error)}</div> : null}

      <section className="card formCard">
        <div className="cardTitle">Info</div>
        <div className="formGrid">
          <div className="formField">
            <span className="formLabel">QA ID</span>
            <span className="mono">{tester.qaId}</span>
          </div>
          <div className="formField">
            <span className="formLabel">Started</span>
            <span>{formatDate(tester.startTime)}</span>
          </div>
          {tester.endTime ? (
            <div className="formField">
              <span className="formLabel">Stopped</span>
              <span>{formatDate(tester.endTime)}</span>
            </div>
          ) : null}
        </div>
      </section>

      <section className="card formCard cardSectionTop">
        <div className="cardTitle">{isActive ? 'Edit' : 'View'}</div>
        <form className="formGrid" onSubmit={handleSave}>
          <label className="formField">
            <span className="formLabel">Remote config</span>
            <select className="textInput" value={values.remoteConfigId} onChange={onChange('remoteConfigId')} disabled={!isActive}>
              <option value="">Choose remote config</option>
              {refs.remoteConfigs.map((rc) => (
                <option key={rc.id} value={String(rc.id)}>
                  {rc.bundleVersion || rc.id} ({rc.status || 'Draft'})
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Segmentation</span>
            <select className="textInput" value={values.segmentationId} onChange={onChange('segmentationId')} disabled={!isActive}>
              <option value="">Without segmentation</option>
              {availableSegmentations.map((s) => (
                <option key={s.id} value={String(s.id)}>{s.name || s.id}</option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Expire (seconds)</span>
            <input className="textInput" type="number" min="1" value={values.expire} onChange={onChange('expire')} disabled={!isActive} />
          </label>

          <label className="formField formFieldFullWidth">
            <span className="formLabel">Description</span>
            <textarea className="textInput textAreaMin90" value={values.description} onChange={onChange('description')} disabled={!isActive} />
          </label>

          <div className="formField formFieldFullWidth">
            <span className="formLabel">AB tests (max 3)</span>
            {abTestRows.map((row) => {
              const selectedTest = refs.abTests.find((t) => String(t.id) === String(row.abTestId))
              const variants = selectedTest?.variants || []
              return (
                <div key={row._id} className="builderRuleGridWide sectionTop10">
                  <select className="textInput" value={row.abTestId} onChange={(e) => changeAbTestRow(row._id, 'abTestId', e.target.value)} disabled={!isActive}>
                    <option value="">Select AB test</option>
                    {refs.abTests.map((t) => (
                      <option key={t.id} value={String(t.id)}>{t.name || t.id}</option>
                    ))}
                  </select>
                  <select className="textInput" value={row.abVariantId} onChange={(e) => changeAbTestRow(row._id, 'abVariantId', e.target.value)} disabled={!isActive}>
                    <option value="">Select variant</option>
                    {variants.map((v) => (
                      <option key={v.id} value={String(v.id)}>{formatVariantLabel(v)}</option>
                    ))}
                  </select>
                  {isActive ? (
                    <button type="button" className="button buttonGhost buttonDanger" onClick={() => removeAbTestRow(row._id)}>Remove</button>
                  ) : null}
                </div>
              )
            })}
            {isActive && abTestRows.length < 3 ? (
              <div className="sectionTop10">
                <button type="button" className="button buttonGhost" onClick={addAbTestRow}>Add AB test</button>
              </div>
            ) : null}
          </div>

          <div className="formField formFieldFullWidth">
            <span className="formLabel">User parameters (max 5)</span>
            {userParamRows.map((row) => (
              <div key={row._id} className="builderRuleGridWide sectionTop10">
                <select className="textInput" value={row.paramType} onChange={(e) => changeUserParamRow(row._id, 'paramType', e.target.value)} disabled={!isActive}>
                  <option value="">Select param</option>
                  {USER_PARAM_TYPES.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
                <input className="textInput" value={row.paramValue} onChange={(e) => changeUserParamRow(row._id, 'paramValue', e.target.value)} placeholder="Value" disabled={!isActive} />
                {isActive ? (
                  <button type="button" className="button buttonGhost buttonDanger" onClick={() => removeUserParamRow(row._id)}>Remove</button>
                ) : null}
              </div>
            ))}
            {isActive && userParamRows.length < 5 ? (
              <div className="sectionTop10">
                <button type="button" className="button buttonGhost" onClick={addUserParamRow}>Add parameter</button>
              </div>
            ) : null}
          </div>

          {isActive ? (
            <div className="formActions">
              <button type="submit" className="button buttonPrimary" disabled={saving}>
                {saving ? 'Saving...' : 'Save & resync'}
              </button>
            </div>
          ) : null}
        </form>
      </section>
    </div>
  )
}
